import { LOCATION_CHANGE, push } from 'connected-react-router'
import { Action } from 'redux-actions'
import { all, put, select, takeLatest } from 'redux-saga/effects'
import { OnboardingStatus, Role, UserProfileDto } from '../../services/api.types'
import { redirectUrl } from '../helpers/routeHelpers'
import { addNotification, clearNotification } from '../notifications/NotificationActions'
import { INotification } from '../notifications/NotificationReducers'
import { ROUTES } from './routePaths'
import { reLocate } from './RouterActions'
import { getCurrentRoute } from './RouterSelector'
import {
  getUserDto,
  hasRole,
  isAuthenticated,
  isEmailVerificationInProgress,
  isResetPasswordInProgress,
} from '../auth/authSelectors'

export function* sagaLocationChange({ payload: { location } }: Action<any>) {
  /**
   * Pass notification to LOCATION_CHANGE event to show it after LOCATION_CHANGE
   * ex. yield put(push(ROUTES.ADMIN_USER.to, {notification}))
   */
  const notification: INotification =
    location && location.state && location.state.notification ? location.state.notification : null

  try {
    /**
     * After every LOCATION_CHANGE notifications are cleared.
     */
    yield put({ type: String(clearNotification) })

    if (notification) {
      yield put({ type: String(addNotification), payload: notification })
    }

    // Guard against bypassing the email-verification step by typing a URL manually: if the
    // session is mid-verification, any destination other than /verify-email is redirected.
    const emailVerificationInProgress: boolean = yield select(isEmailVerificationInProgress)
    if (emailVerificationInProgress && location && location.pathname !== ROUTES.VERIFY_EMAIL.to) {
      yield put(push(ROUTES.VERIFY_EMAIL.to))
    }

    // Keep the `logged-in` body class in sync with the new route: /verify-email must not
    // get it (the view has its own minimal chrome), but leaving the route should restore it.
    const isLoggedIn: boolean = yield select(isAuthenticated)
    const newPathname = location && location.pathname
    const isVerifyEmail = newPathname === ROUTES.VERIFY_EMAIL.to
    const isLeaderboard = typeof newPathname === 'string' && newPathname.startsWith('/leaderboard/')
    if (isLoggedIn && !isVerifyEmail && !isLeaderboard) {
      document.body.classList.add('logged-in')
    } else {
      document.body.classList.remove('logged-in')
    }
  } catch (e: any) {
    console.log('Router error', e)
  }
}

export function* sagaRelocate(action?: Action<any>) {
  const { pathname } = yield select(getCurrentRoute)
  /**
   * Optional notification to show on the destination view, e.g.
   * `put({ type: String(reLocate), payload: { notification } })`. It travels in
   * the push location state so sagaLocationChange re-adds it after clearing
   * notifications on LOCATION_CHANGE. Dispatching addNotification around
   * reLocate instead would race with the navigation and get cleared.
   */
  const notification: INotification | null = (action && action.payload && action.payload.notification) || null
  const navState = notification ? { notification } : undefined
  let navigated = false
  try {
    // Select the new data.
    const userDto: UserProfileDto = yield select(getUserDto)
    const resetPasswordInProgress: boolean = yield select(isResetPasswordInProgress)
    const emailVerificationInProgress: boolean = yield select(isEmailVerificationInProgress)
    //const isAnonymous: boolean = yield select(hasRole, Role.ANONYMOUS)
    const isLoggedIn: boolean = yield select(isAuthenticated)
    const isVerifyEmail = pathname === ROUTES.VERIFY_EMAIL.to
    if (!isLoggedIn || isVerifyEmail) {
      document.body.classList.remove('logged-in')
    } else {
      document.body.classList.add('logged-in')
      document.body.classList.remove('logging-in')
    }
    const isAdmin: boolean = yield select(hasRole, Role.ADMIN)

    const { onboardingStatus } = userDto || { onboardingStatus: null }
    const { REGISTER_USER, RESET_PASSWORD, VERIFY_EMAIL } = ROUTES

    const isOnboardingFlow =
      onboardingStatus === OnboardingStatus.PROFILE_DATA ||
      onboardingStatus === OnboardingStatus.ACTIVITY_DATA ||
      onboardingStatus === OnboardingStatus.NOTIFICATION_PREFERENCES ||
      onboardingStatus === OnboardingStatus.INTEGRATION ||
      onboardingStatus === OnboardingStatus.PROFILE_SYNC_IN_PROGRESS

    if (emailVerificationInProgress) {
      if (pathname !== VERIFY_EMAIL.to) {
        yield put(push(VERIFY_EMAIL.to, navState))
        navigated = true
      }
    } else if (resetPasswordInProgress) {
      if (pathname !== RESET_PASSWORD.to) {
        yield put(push(RESET_PASSWORD.to, navState))
        navigated = true
      }
    } else if (!onboardingStatus && !isAdmin) {
      // Do nothing
    } else if (isOnboardingFlow) {
      if (pathname !== REGISTER_USER.to) {
        yield put(push(REGISTER_USER.to, navState))
        navigated = true
      }
    } else if (isLoggedIn && pathname !== redirectUrl()) {
      yield put(push(redirectUrl(), navState))
      navigated = true
    } else {
      // Do nothing
    }

    // Already on the destination (e.g. a concurrent reLocate navigated first):
    // there is no LOCATION_CHANGE to carry the notification, so add it directly.
    if (!navigated && notification) {
      yield put({ type: String(addNotification), payload: notification })
    }
  } catch (e: any) {
    console.log('relocateFailed', e)
    if (pathname !== ROUTES.LANDING.to) {
      yield put(push(ROUTES.LANDING.to))
    }
  }
}

export function* routerSaga() {
  yield all([takeLatest(String(LOCATION_CHANGE), sagaLocationChange), takeLatest(String(reLocate), sagaRelocate)])
}
